import { DateTime } from "luxon";
import { useState } from "react";

/** Default trailing 6-month window (today minus 6 months → today). */
function defaultTrailingSixMonths(): { startDate: string; endDate: string } {
  // Anchor to the LOCAL calendar date (matching `WasteFacilityFilters`).
  // Formatting a local Luxon DateTime preserves that calendar date in every
  // timezone — unlike `Date#toISOString()`, which converts to UTC and can roll
  // the date forward (US zones, late in the day) or backward (UTC+ zones).
  const end = DateTime.local().startOf("day");
  const start = end.minus({ months: 6 });
  return {
    startDate: start.toFormat("yyyy-MM-dd"),
    endDate: end.toFormat("yyyy-MM-dd"),
  };
}

/**
 * User-adjustable date range for the consolidated `WasteActivityStep`, where the
 * user controls the window that bounds the Generator Status and Waste Streams
 * tiles. Defaults to the trailing 6 months (today minus 6 months → today);
 * `setRange` replaces both ends at once.
 *
 * Returns ISO `YYYY-MM-DD` strings. It does not derive from the submission's
 * reporting period — it is purely user-driven so they can widen/narrow the
 * activity window during review.
 */
export function useAdjustableStepWasteDateRange(): {
  startDate: string;
  endDate: string;
  setRange: (next: { startDate: string; endDate: string }) => void;
} {
  // The `useState` setter is referentially stable and already takes the exact
  // `{ startDate, endDate }` shape, so it can be returned directly as `setRange`
  // without a `useCallback` wrapper.
  const [range, setRange] = useState<{ startDate: string; endDate: string }>(
    defaultTrailingSixMonths
  );

  return { startDate: range.startDate, endDate: range.endDate, setRange };
}
